import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { first, tap, map } from 'rxjs';
import { forkJoin, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { OrderTermsJeevesComponent } from 'gung-standard-jeeves';
import {
  AvailabilityService,
  PriceService,
  AuthService,
  ProductService,
  Product,
  SelectedCustomerService,
  CartService,
  CartRow,
  CheckoutRow,
  NavbarCartItem,
  Availability,
  Customer
} from 'gung-standard';
import { TyloFreightCostService } from '../../../../services/tylo-freight-cost.service';
import { parse } from 'date-fns';
import { TyloCustomerUtilsService } from '../../../../services/utils/tylo-customer-utils.service';
import { environment } from './../../../../../environments/environment';

interface Alert {
  type: string;
  message: string;
}

const getMinDate = (av: Availability, quantity: number): Date => {
  if (av.currentAvailability > 0) {
    return new Date();
  }
  if (av.maxFutureStock === 0) {
    return new Date();
  }
  const valids = Object.keys(av.availabilities).filter(entry => av.availabilities[entry] >= quantity);
  const dateFormat = 'yyMMdd';
  return valids.length > 0 ? parse(valids[0], dateFormat, new Date()) : new Date();
};

@Component({
    selector: 'tylo-order-terms-jeeves',
    templateUrl: './tylo-order-terms-jeeves.component.html',
    styleUrls: ['./tylo-order-terms-jeeves.component.css'],
    standalone: false
})
export class TyloOrderTermsJeevesComponent extends OrderTermsJeevesComponent implements OnInit {
  private freightCostProductId = '00000023'; // Uppercase is important
  alertMessageFreightCost: Alert;
  alertMessageAdminFee: Alert;
  deliveryMethodEntries: { id: string; name: string }[] = [];
  partDeliveryEntries: { id: string; name: string }[] = [];
  orderWeight = 0;
  orderVolume = 0;
  public isExportCustomer: boolean;
  public shouldFreightBeApplied: boolean;

  isUser: boolean;

  constructor(
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected datePipe: DatePipe,
    protected priceService: PriceService,
    protected availabilityService: AvailabilityService,
    protected productService: ProductService,
    private tyloFreightCostService: TyloFreightCostService,
    private translateService: TranslateService,
    protected selectedCustomerService: SelectedCustomerService,
    protected cartService: CartService,
    protected tyloCustomerUtilsService: TyloCustomerUtilsService
  ) {
    super(authService, formBuilder, datePipe, availabilityService);
    // FC: in the constructor is possible that the checkout dont exists
    // selectedCustomerService.getSelectedCustomer().subscribe(selectedCustomer => {
    //   this.checkout.extra.customerVATCode = this.tyloCustomerUtilsService.getCustomerVATCode(selectedCustomer);
    // });
  }

  ngOnInit() {
    super.ngOnInit();

    this.authService.getRoles().subscribe(roles => {
      this.isUser = roles.length === 1 && roles.includes('USER');
    });

    forkJoin({
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      products: this.productService.getFullProductsByIds(this.checkout.rows.map(row => row.id)).pipe(first())
    })
      .pipe(first())
      .subscribe(({ selectedCustomer, products }) => {
        this.checkout.extra.customerVATCode = this.tyloCustomerUtilsService.getCustomerVATCode(selectedCustomer);
        this.calulcateOrderWeight(products);
        this.calulcateOrderVolume(products);
        this.setDeliveryMethodValues(selectedCustomer);
        this.calculateFreightCost();
      });
    this.calculateAdminFee();
    this.initPartialDeliveries();
    for (const row of this.checkout.rows) {
      row.extra._user_selected_ordberlevdat = row.extra._user_selected_ordberlevdat || row.extra.orp.ordberlevdat;
    }
  }

  dateSelected(event) {
    const date = event.date;
    const dString: string = this.datePipe.transform(date, 'yyyy-MM-dd');
    this.checkout.extra.oh.ordberlevdat = dString;
    this.createFullDeliveryPlan();
  }

  createFullDeliveryPlan() {
    for (const row of this.checkout.rows) {
      const deliveryPlan = {};
      deliveryPlan[this.checkout.extra.oh.ordberlevdat] = row.quantity;
      // This is important because used by backend to split lines
      row.extra._deliveryPlan = deliveryPlan;
    }
  }
  protected initForm() {
    this.form = this.formBuilder.group({
      dellevtillaten: this.formBuilder.control(this.checkout.extra.oh.dellevtillaten || '0'),
      levsattkod: this.formBuilder.control(this.checkout.extra.procargs.levsattkod || ''),
      levvillkkod: this.formBuilder.control({ value: this.checkout.extra.procargs.levvillkkod || '', disabled: true }),
      betkod: this.formBuilder.control(this.checkout.extra.oh.betkod || ''),
      saljare: this.formBuilder.control(this.checkout.extra.procargs.saljare || '')
    });
  }
  private initPartialDeliveries(): void {
    this.partDeliveryEntries = [
      { id: '0', name: 'TYLO_OH_DELLEVTILLATEN_0' },
      { id: '10', name: 'TYLO_OH_DELLEVTILLATEN_10' }
    ];
    this.assignRowDates();
  }

  assignRowDates(): void {
    if (!this.checkout.rows?.length) {
      return;
    }
    this.availabilityService
      .getAvailabilities(
        this.checkout.rows.map(r => r.id),
        undefined,
        true
      )
      .pipe(map(avs => avs.map(av => ({ ...av, availabilities: av.extra.availabilities }))))
      .subscribe(avs => {
        for (const row of this.checkout.rows) {
          const av = avs.find(a => a.productId === row.id);
          row.extra._availability_reference = av;
          let mindate = null;
          if (av) {
            const tempDate = getMinDate(av, 1);
            let minDate = new Date();
            if (minDate < tempDate) {
              minDate = tempDate;
            }
            const dString: string = this.datePipe.transform(minDate, 'yyyy-MM-dd');
            mindate = dString;
          }
          row.extra._calc_min_date = mindate;
          row.extra.orp.ordberlevdat = row.extra._calc_min_date;
          if (row.extra._user_selected_ordberlevdat) {
            if (row.extra._user_selected_ordberlevdat > row.extra._calc_min_date) {
              row.extra.orp.ordberlevdat = row.extra._user_selected_ordberlevdat;
            }
          }

          const deliveryPlan = {};
          const dates = Object.keys(av.availabilities);
          let quantityRemaining = row.quantity;
          for (const date of dates) {
            const qty = av.availabilities[date];
            if (qty > 0 && quantityRemaining > 0) {
              const parsedDate = parse(date, 'yyMMdd', new Date());
              const formattedDate = this.datePipe.transform(parsedDate, 'yyyy-MM-dd');

              let assignedDate = formattedDate;
              if (row.extra._user_selected_ordberlevdat > assignedDate) {
                assignedDate = row.extra._user_selected_ordberlevdat;
              }
              const currentOnDate = deliveryPlan[assignedDate] || 0;
              const currentAllocation = Math.min(qty, quantityRemaining);

              deliveryPlan[assignedDate] = currentOnDate + currentAllocation;

              quantityRemaining = quantityRemaining - currentAllocation;
            }
          }
          if (quantityRemaining > 0) {
            // Handle edge case of last quantities of exporing product
            deliveryPlan['2099-12-31'] = quantityRemaining;
          }
          row.extra._deliveryPlan = deliveryPlan;
        }
      });
  }

  clearRowDates(): void {
    for (const row of this.checkout.rows) {
      delete row.extra.orp.ordberlevdat;
    }
  }

  handlePartDeliveryChange(): void {
    const partDeliveryFormControlName = 'dellevtillaten';
    const selectedPArtDeliveryValue = this.form.controls[partDeliveryFormControlName].value || '';
    this.checkout.extra.oh.dellevtillaten = selectedPArtDeliveryValue;
    if (this.checkout.extra.oh.dellevtillaten === '0') {
      delete this.checkout.extra.oh.ordberlevdat;
      this.assignRowDates();
    } else {
      delete this.minDate;
      this.availabilityService
        .getAvailabilities(
          this.checkout.rows.map(r => r.id),
          undefined,
          true
        )
        .pipe(map(avs => avs.map(av => ({ ...av, availabilities: av.extra.availabilities }))))
        .subscribe(avs => {
          let minDateFromAvailability = new Date();

          let maxAssignedDate = '1970-01-01';
          for (const row of this.checkout.rows) {
            const av = avs.find(a => a.productId === row.id);
            if (av) {
              const tempDate = getMinDate(av, row.quantity);
              if (minDateFromAvailability < tempDate) {
                minDateFromAvailability = tempDate;
              }
            }
            if (row.extra._user_selected_ordberlevdat && row.extra._user_selected_ordberlevdat > maxAssignedDate) {
              maxAssignedDate = row.extra._user_selected_ordberlevdat;
            }
          }
          this.minDate = minDateFromAvailability;

          const dString: string = this.datePipe.transform(this.minDate, 'yyyy-MM-dd');

          console.log('maxAssignedDate', maxAssignedDate);
          console.log('dString', dString);
          if (dString > maxAssignedDate) {
            this.checkout.extra.oh.ordberlevdat = dString;
          } else {
            this.checkout.extra.oh.ordberlevdat = maxAssignedDate;
            this.minDate = parse(maxAssignedDate, 'yyyy-MM-dd', this.minDate);
          }
          this.createFullDeliveryPlan();
        });
    }
  }

  calculateFreightCost(): void {
    this.alertMessageFreightCost = null;

    const deliveryMethodFormControlName = 'levsattkod';
    const selectedLevsattkodValue = this.form.controls[deliveryMethodFormControlName].value || '';

    // https://gung-se.freshdesk.com/a/tickets/134124
    const fraktkol3limit = Math.max(this.orderWeight, this.orderVolume);
    // const fraktkol3limit = this.orderWeight > this.orderVolume ? this.orderVolume : this.orderWeight

    forkJoin({
      freightCost: this.tyloFreightCostService.getFreightCost(selectedLevsattkodValue, fraktkol3limit).pipe(first()),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
    })
      .pipe(first())
      .subscribe(({ freightCost, selectedCustomer }) => {
        this.checkout.extra.additionalCosts = {
          ...this.checkout.extra.additionalCosts,
          freight: null
        };

        this.shouldFreightBeApplied = true;
        // Add delivery method = 3 as always freight free if no delivery address change. Just like we have with delivery method 99 now.
        if (!this.checkout.extra._deliveryAddressEdited && this.checkout.extra.procargs.levvillkkod === 3) {
          this.shouldFreightBeApplied = false;
          return;
        }
        // Dont add freight for som customers (Export) or delivery terms (1 - Freight Free)
        if (
          !this.tyloCustomerUtilsService.shouldFreightByApplied(
            selectedCustomer,
            this.checkout.extra.procargs.levvillkkod
          )
        ) {
          this.shouldFreightBeApplied = false;
          return;
        }

        // In case freight cost value is defined
        // Then add to cart item freightCostProductId and show message about freight cost
        // otherwise remove item freightCostProductId from cart if already in the cart
        if (!!freightCost.value && freightCost.value > -1) {
          const freightCostValue = {
            productId: this.freightCostProductId,
            value: freightCost.value,
            currencyCode: selectedCustomer.extra.kus.valkod,
            quantity: 1
          };

          // add it to extra in checkout to use it in summarize step
          this.checkout.extra.additionalCosts = {
            ...this.checkout.extra.additionalCosts,
            freight: freightCostValue
          };

          // show alert message about the freight cost
          this.alertMessageFreightCost = {
            type: 'success',
            message: this.translateService.instant('TYLO_FREIGHT_COST', {
              value: freightCostValue.value,
              currencyCode: freightCostValue.currencyCode
            })
          };
        } else {
          this.alertMessageFreightCost = {
            type: 'success',
            message: this.translateService.instant('TYLO_FREIGHT_COST_ERROR')
          };
        }
      });
  }

  protected calulcateOrderWeight(products: Product[]): void {
    // artbtotvikt
    products
      .filter(product => !!product.extra.ar.artbtotvikt)
      .forEach(product => {
        const quantity = this.checkout.rows.find(row => row.id === product.id).quantity;
        this.orderWeight += quantity * parseFloat(product.extra.ar.artbtotvikt);
      });
  }

  protected calulcateOrderVolume(products: Product[]): void {
    // artvolym
    products
      .filter(product => !!product.extra.ar.artvolym)
      .forEach(product => {
        const quantity = this.checkout.rows.find(row => row.id === product.id).quantity;
        this.orderVolume += quantity * parseFloat(product.extra.ar.artvolym);
      });

    this.orderVolume *= 280;
  }

  protected setDeliveryMethodValues(customer: Customer): void {
    const deliveryMethodFormControlName = 'levsattkod';

    // if kus.levsattkod = 6 then set field to read only
    // if kus.levsattkod = 1 open field for selection
    // if kus.levsattkod = 5 open field for selection
    // If weight >= 10 kg then only select 1 or 5
    // If weight < 10kg then open up for select 1,5,66,67

    if (this.checkout.extra.procargs.levsattkod === 6) {
      this.form.controls[deliveryMethodFormControlName].disable();
    } else {
      this.form.controls[deliveryMethodFormControlName].enable();
    }

    if (this.checkout.extra.customerVATCode === 7) {
      this.deliveryMethodEntries = [
        { id: '80', name: 'metadata.x2f.80.levsattbeskr' },
        { id: '83', name: 'metadata.x2f.83.levsattbeskr' },
        { id: '81', name: 'metadata.x2f.81.levsattbeskr' },
        { id: '75', name: 'metadata.x2f.75.levsattbeskr' }
      ];
    } else if (this.checkout.extra.customerVATCode === 9) {
      this.deliveryMethodEntries = [
        { id: '85', name: 'metadata.x2f.85.levsattbeskr' },
        { id: '88', name: 'metadata.x2f.88.levsattbeskr' },
        { id: '86', name: 'metadata.x2f.86.levsattbeskr' },
        { id: '75', name: 'metadata.x2f.75.levsattbeskr' }
      ];
    } else {
      if (this.orderWeight >= 10) {
        this.deliveryMethodEntries = [
          { id: '1', name: 'metadata.x2f.1.levsattbeskr' },
          { id: '5', name: 'metadata.x2f.5.levsattbeskr' }
        ];
      } else {
        this.deliveryMethodEntries = [
          { id: '1', name: 'metadata.x2f.1.levsattbeskr' },
          { id: '5', name: 'metadata.x2f.5.levsattbeskr' },
          { id: '66', name: 'metadata.x2f.66.levsattbeskr' },
          { id: '67', name: 'metadata.x2f.67.levsattbeskr' }
        ];
      }
    }

    if (
      customer.extra.kus.levsattkod &&
      this.deliveryMethodEntries.findIndex(d => d.id === customer.extra.kus.levsattkod + '') === -1
    ) {
      this.deliveryMethodEntries.push({
        id: customer.extra.kus.levsattkod + '',
        name: 'metadata.x2f.' + customer.extra.kus.levsattkod + '.levsattbeskr'
      });
    }
  }

  /**
   * calculateAdminFee - Check min value and add admin charge
   */
  private calculateAdminFee() {
    this.checkout.extra.additionalCosts = {
      ...this.checkout.extra.additionalCosts,
      adminFee: null
    };

    // 20201211 - NEVER ADD THE ADMIN FEE
    return;

    this.cartService
      .getCurrentCart()
      .pipe(first())
      .subscribe(cart => {
        const idQtyMap = cart.reduce((acc, curr) => ({ ...acc, [curr.productId]: curr.qty }), {});
        const ids = Object.keys(idQtyMap);
        forkJoin({
          rows: of(cart),
          products: ids.length > 0 ? this.productService.getFullProductsByIds(ids).pipe(first()) : of([]),
          prices: this.priceService.getCartRowPricesObservable(cart).pipe(first())
        })
          .pipe(
            first(),
            map(({ rows, products, prices }) => {
              const toReturnRows = rows.map(row => {
                const rowProduct = products.find(p => p.id === row.productId);
                const rowPrice = prices.find(r => r.productId === row.productId);
                if (!rowProduct || !rowPrice) {
                  throw new Error('Could not find mapped product or price');
                }
                const data: NavbarCartItem = {
                  product: rowProduct,
                  price: rowPrice,
                  cartRow: row
                };
                return data;
              });
              const totalValue = toReturnRows
                .map(row => row.price.cartRowUnitPriceInclRowDiscount.value * row.cartRow.qty)
                .reduce((acc, curr) => acc + curr, 0);
              const currency = prices.length > 0 ? prices[0].customerNetPrice.currencyCode : '';

              switch (currency) {
                case 'SEK':
                  if (totalValue < 300) {
                    this.alertMessageAdminFee = {
                      type: 'warning',
                      message: this.translateService.instant('TYLO_MIN_ORDER_ADMIN_FEE', {
                        min: 300 + currency,
                        fee: 50 + currency
                      })
                    };
                    // add it to extra in checkout to use it in summarize step
                    this.checkout.extra.additionalCosts = {
                      ...this.checkout.extra.additionalCosts,
                      adminFee: {
                        min: 300,
                        fee: 50,
                        currency
                      }
                    };
                  }
                  break;
                case 'EUR':
                case 'USD':
                case 'GBP':
                  if (totalValue < 30) {
                    this.alertMessageAdminFee = {
                      type: 'warning',
                      message: this.translateService.instant('TYLO_MIN_ORDER_ADMIN_FEE', {
                        min: 30 + currency,
                        fee: 5 + currency
                      })
                    };
                    // add it to extra in checkout to use it in summarize step
                    this.checkout.extra.additionalCosts = {
                      ...this.checkout.extra.additionalCosts,
                      adminFee: {
                        min: 30,
                        fee: 5,
                        currency
                      }
                    };
                  }
                  break;
              }
            })
          )
          .subscribe();
      });
  }
}
